import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  IconButton,
  Chip,
  Autocomplete,
  Stack,
  Divider,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import { Add, Search, Phone, Person, FiberManualRecord, CalendarToday } from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useVendorLedgerBalanceCalculator } from '../../../../../utils/useVendorLedgerBalanceCalculator';
import { request } from '../../../../../utils/request';

import usePrint from '../../../../../utils/usePrint';
import PrintableContainer from '../../../../../components/PrintableContainer';
import PrintButtons from '../../../../../components/PrintButtons';
import { DailyLedger, DailyLedgerWithBalance, VendorInfo, LedgerSummary } from '../types/dailyLedger';
import CommonLedgerTable from '../../../../../components/CommonLedgerTable';
import { useGstMode } from '../../../../../utils/useGstMode';

const DailyLedgerList: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [ledgers, setLedgers] = useState<DailyLedger[]>([]);
  const [filteredLedgers, setFilteredLedgers] = useState<DailyLedgerWithBalance[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [dateRange, setDateRange] = useState<string>('all');
  const [vendorInfo, setVendorInfo] = useState<VendorInfo | null>(null);
  const [allVendors, setAllVendors] = useState<VendorInfo[]>([]);
  const [showVendorSelect, setShowVendorSelect] = useState<boolean>(false);
  
  // Summary for manual ledger entries (from API)
  const [manualLedgerSummary, setManualLedgerSummary] = useState<LedgerSummary>({
    totalCredit: 0,
    totalDebit: 0,
    balance: 0
  });

  // Separate summaries for each auto-generated ledger type
  const [gstLedgerSummary, setGstLedgerSummary] = useState<LedgerSummary>({
    totalCredit: 0,
    totalDebit: 0,
    balance: 0
  });

  const [nonGstLedgerSummary, setNonGstLedgerSummary] = useState<LedgerSummary>({
    totalCredit: 0,
    totalDebit: 0,
    balance: 0
  });

  // Combined overall summary
  const [overallSummary, setOverallSummary] = useState<LedgerSummary>({
    totalCredit: 0,
    totalDebit: 0,
    balance: 0
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [ledgerToDelete, setLedgerToDelete] = useState<string | null>(null);

  // Custom date range states
  const [customDateDialogOpen, setCustomDateDialogOpen] = useState<boolean>(false);
  const [customStartDate, setCustomStartDate] = useState<Date | null>(null);
  const [customEndDate, setCustomEndDate] = useState<Date | null>(null);

  const { isPrinting, handlePrint } = usePrint();
  const { gstEnabled } = useGstMode();

  // Calculate overall summary when any of the individual summaries change
  useEffect(() => {
    const totalDebit = manualLedgerSummary.totalDebit + gstLedgerSummary.totalDebit + nonGstLedgerSummary.totalDebit;
    const totalCredit = manualLedgerSummary.totalCredit + gstLedgerSummary.totalCredit + nonGstLedgerSummary.totalCredit;
    const balance = manualLedgerSummary.balance + gstLedgerSummary.balance + nonGstLedgerSummary.balance;
    
    setOverallSummary({ totalDebit, totalCredit, balance });
  }, [manualLedgerSummary, gstLedgerSummary, nonGstLedgerSummary]);

  // print handler
  const onPrint = () => {
    // force re-render before invoking print
    requestAnimationFrame(() => {
      setTimeout(() => {
        handlePrint('daily-ledger-print', {
          title: `Daily Ledger - ${vendorInfo?.vendorName || 'Vendor'}`,
          fileName: `DailyLedger_${vendorInfo?.vendorName || 'Report'}`,
          landscape: false,
          showHeader: true,
          showFooter: false,
          customStyles: `
          .balance-positive { color: #2e7d32 !important; font-weight: bold !important; }
          .balance-negative { color: #d32f2f !important; font-weight: bold !important; }
          table { width: 100% !important; border-collapse: collapse !important; }
          th, td { border: 1px solid #ddd !important; padding: 6px !important; }
          th { background-color: #f5f5f5 !important; font-weight: bold !important; }
          .no-print { display: none !important; }
        `
        });
      }, 150); // small delay ensures browser repaints
    });
  };

  const { calculateRunningBalance } = useVendorLedgerBalanceCalculator();

  // Fetch all vendors for dropdown
  const fetchAllVendors = useCallback(async (): Promise<void> => {
    try {
      const response = await request({
        url: 'vendor/get-all-vendors',
        method: 'get',
      });
      if (response.status === "success") {
        setAllVendors(response.vendors || []);
      }
    } catch (error: any) {
      toast.error(error || 'Failed to fetch vendors');
      console.error('Error fetching vendors:', error);
    }
  }, []);

  // Fetch vendor information
  const fetchVendorInfo = useCallback(async (id?: string): Promise<void> => {
    const targetVendorId = id || vendorId;
    if (!targetVendorId) return;

    try {
      const response = await request({
        url: `vendor/get-vendor/${targetVendorId}`,
        method: 'get',
      });
      if (response.status === "success") {
        setVendorInfo(response.vendor);
      }
    } catch (error: any) {
      toast.error(error || 'Failed to fetch vendor information');
      console.error('Error fetching vendor info:', error);
    }
  }, [vendorId]);

  // Fetch ledger data
  const fetchLedgers = useCallback(async (id?: string): Promise<void> => {
    const targetVendorId = id || vendorId;
    if (!targetVendorId) return;

    setLoading(true);
    try {
      const params: { search?: string } = {};
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await request({
        url: `vendorDailyLedger/get-vendor-ledger/${targetVendorId}`,
        method: 'get',
        params
      });
      if (response.success) {
        const ledgersWithBalance = calculateRunningBalance(response.data);
        setLedgers(response.data);
        setFilteredLedgers(ledgersWithBalance);
      } else {
        console.error(response.message || "Something wrong to fetch vendor daily ledger")
        toast.error(response.message || "Something wrong to fetch vendor daily ledger")
      }

    } catch (error: any) {
      toast.error(error || 'Failed to fetch ledger data');
      console.error('Error fetching ledgers:', error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, vendorId, calculateRunningBalance]);

  // Fetch ledger summary for manual entries
  const fetchLedgerSummary = useCallback(async (id?: string): Promise<void> => {
    const targetVendorId = id || vendorId;
    if (!targetVendorId) return;

    try {
      const response = await request({
        url: `vendorDailyLedger/get-vendor-ledger-summary/${targetVendorId}`,
        method: 'get',
      });
      if (response.success) {
        setManualLedgerSummary(response.data);
      }
    } catch (error: any) {
      toast.error(error || 'Failed to fetch ledger summary');
      console.error('Error fetching summary:', error);
    }
  }, [vendorId]);

  useEffect(() => {
    if (vendorId) {
      fetchAllVendors();
      fetchVendorInfo();
      fetchLedgers();
      fetchLedgerSummary();
    }
  }, [vendorId, fetchAllVendors, fetchVendorInfo, fetchLedgers, fetchLedgerSummary]);

  // Filter ledgers based on date range
  useEffect(() => {
    const filterByDateRange = (ledgersToFilter: DailyLedger[]): DailyLedgerWithBalance[] => {
      if (dateRange === 'all') return calculateRunningBalance(ledgersToFilter);

      const now = new Date();
      let startDate = new Date();

      if (dateRange === 'custom' && customStartDate && customEndDate) {
        // Use custom date range
        startDate = new Date(customStartDate);
        startDate.setHours(0, 0, 0, 0);
        
        const endDate = new Date(customEndDate);
        endDate.setHours(23, 59, 59, 999);

        const filtered = ledgersToFilter.filter(ledger => {
          if (!ledger.date) return false;
          const ledgerDate = new Date(ledger.date);
          return ledgerDate >= startDate && ledgerDate <= endDate;
        });

        return calculateRunningBalance(filtered);
      }

      switch (dateRange) {
        case '3months':
          startDate.setMonth(now.getMonth() - 3);
          break;
        case '6months':
          startDate.setMonth(now.getMonth() - 6);
          break;
        case '1year':
          startDate.setFullYear(now.getFullYear() - 1);
          break;
        default:
          return calculateRunningBalance(ledgersToFilter);
      }

      const filtered = ledgersToFilter.filter(ledger => {
        if (!ledger.date) return false;
        const ledgerDate = new Date(ledger.date);
        return ledgerDate >= startDate;
      });

      return calculateRunningBalance(filtered);
    };

    const filtered = filterByDateRange(ledgers);
    setFilteredLedgers(filtered);
  }, [dateRange, ledgers, calculateRunningBalance, customStartDate, customEndDate]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchTerm(e.target.value);
  };

  const handleSearchSubmit = (e: React.KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === 'Enter') {
      fetchLedgers();
    }
  };

  const handleDateRangeChange = (e: SelectChangeEvent<string>): void => {
    const value = e.target.value;
    if (value === 'custom') {
      setCustomDateDialogOpen(true);
    } else {
      setDateRange(value);
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  const handleCustomDateApply = (): void => {
    if (customStartDate && customEndDate) {
      setDateRange('custom');
      setCustomDateDialogOpen(false);
    } else {
      toast.warning('Please select both start and end dates');
    }
  };

  const handleCustomDateCancel = (): void => {
    setCustomDateDialogOpen(false);
    // Reset to previous selection if no dates selected
    if (!customStartDate || !customEndDate) {
      setCustomStartDate(null);
      setCustomEndDate(null);
    }
  };

  const handleVendorChange = (newVendorId: string): void => {
    if (newVendorId) {
      // Update URL and reload data for new vendor
      navigate(`/vendors/vendor/dailyLedger/${newVendorId}`);
      setShowVendorSelect(false);
    }
  };

  const handleToggleVendorSelect = (): void => {
    setShowVendorSelect(!showVendorSelect);
  };

  const handleAddLedger = (): void => {
    if (vendorId) {
      navigate(`/vendors/vendor/dailyLedger/addDailyLedger/${vendorId}`);
    } else {
      toast.error('Vendor ID is missing');
    }
  };

  const handleDeleteClick = (ledgerId: string): void => {
    setLedgerToDelete(ledgerId);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!ledgerToDelete) return;

    try {
      const res = await request({
        url: `vendorDailyLedger/delete-vendor-ledger/${ledgerToDelete}`,
        method: 'delete',
      });
      if (res.success) {
        toast.success(res.message || 'Ledger entry deleted successfully');
        fetchLedgers();
        fetchLedgerSummary();
      } else {
        toast.error(res.message || 'Something wrong when deleting ledger');
      }
    } catch (error: any) {
      toast.error(error || 'Failed to delete ledger entry');
      console.error('Error deleting ledger:', error);
    } finally {
      setDeleteDialogOpen(false);
      setLedgerToDelete(null);
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteDialogOpen(false);
    setLedgerToDelete(null);
  };

  const formatCurrency = (amount: number | undefined): string => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount || 0);
  };

  const formatDate = (dateString: string | undefined): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN');
  };

  // Show error if no vendorId
  if (!vendorId) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="error">
          Vendor ID is missing from URL parameters
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header Section - EXCLUDE FROM PRINT */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }} className='no-print'>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Daily Ledger
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }} className="no-print">
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleAddLedger}
            size='small'
            sx={{
              bgcolor: 'primary.main',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            Add Ledger
          </Button>
          <PrintButtons
            onPrint={onPrint}
            isPrinting={isPrinting}
            printLabel='Print Ledger'
            pdfLabel='Export PDF'
            disabled={loading || !vendorInfo}
            showPDF={false}
          />
          <Tooltip title="Refresh"><IconButton onClick={() => window.location.reload()}><RotateLeftIcon /></IconButton></Tooltip>
        </Box>
      </Box>

      {/* Printable Content */}
      <PrintableContainer id="daily-ledger-print">
        {/* PRINT-ONLY HEADER */}
        <Box className="print-only" sx={{ textAlign: "center", width: "100%" }} >
          <Typography variant="h1" sx={{ fontWeight: "bold" }}>
            {vendorInfo?.vendorName}
          </Typography>

          <Typography variant='body1' sx={{}}>
            Contact No. : {vendorInfo?.contact?.phone?.[0]?.phoneNo || 'N/A'}
          </Typography>

          <Typography variant="body1">
            Location : {vendorInfo?.location || 'N/A'}
          </Typography>

          <Box sx={{ mt: 2, borderTop: "2px solid #000", display: "flex", flexDirection: "column" }}>
            <Typography variant='body1'>
              {overallSummary.balance >= 0 ? "Total Payable (We owe):" : "Total Receivable (They owe):"}
            </Typography>
            <Typography variant='h2' sx={{ fontWeight: "bold" }}>
              {formatCurrency(Math.abs(overallSummary.balance))}
              <Typography component="span" variant="h6" sx={{ ml: 1 }}>
                {overallSummary.balance >= 0 ? '(Cr)' : '(Dr)'}
              </Typography>
            </Typography>
          </Box>
        </Box>

        {/* Vendor Info and Summary Section */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }} className="no-print">
              {/* Vendor Information */}
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
                  <Person sx={{ color: 'text.secondary' }} className='no-print' />
                  {showVendorSelect ? (
                    <Autocomplete
                      size="small"
                      options={allVendors}
                      value={allVendors.find(vendor => vendor._id === vendorId) || null}
                      getOptionLabel={(option) => option.vendorName}
                      filterOptions={(options, { inputValue }) => {
                        const searchTerm = inputValue.toLowerCase().trim();
                        if (!searchTerm) return options;

                        return options.filter(option =>
                          option.vendorName.toLowerCase().includes(searchTerm)
                        );
                      }}
                      onChange={(_, newValue) => {
                        if (newValue) {
                          handleVendorChange(newValue._id);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Select Vendor"
                          placeholder="Search by name..."
                          className='no-print'
                        />
                      )}
                      renderOption={(props, option) => (
                        <Box component="li" {...props} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1 }}>
                          <Typography variant="body2" sx={{ flex: 1 }}>
                            {option.vendorName}
                          </Typography>
                        </Box>
                      )}
                      sx={{
                        minWidth: 400,
                        '& .MuiAutocomplete-inputRoot': {
                          paddingRight: '14px !important'
                        }
                      }}
                      noOptionsText="No vendors found"
                      isOptionEqualToValue={(option, value) => option._id === value._id}
                      clearOnBlur={false}
                      blurOnSelect={true}
                      autoFocus
                    />
                  ) : (
                    <>
                      <Typography variant="h4" component="h2">
                        {vendorInfo?.vendorName || 'Loading...'}
                      </Typography>
                      <Tooltip title="Switch Vendor" arrow>
                        <IconButton
                          size="small"
                          onClick={handleToggleVendorSelect}
                          className='no-print'
                          sx={{ ml: 1 }}
                        >
                          <ChangeCircleIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <Phone sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} className='no-print' />
                  <Typography variant="body2" color="text.secondary">
                    {vendorInfo?.contact?.phone?.[0]?.phoneNo || 'N/A'}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocationOnIcon sx={{ mr: 1, color: 'text.secondary', fontSize: 18 }} className='no-print' />
                  <Typography variant="body2" color="text.secondary">
                    {vendorInfo?.location || 'N/A'}
                  </Typography>
                </Box>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Ledger Tables */}
        <Box sx={{ display: "flex", flexDirection: 'column', gap: 2 }}>
          {/* Manual Ledger Entries */}
          <Card>
            <CardContent sx={{ p: 0 }}>
              <Typography variant='h6' sx={{ py: 1, px: 1 }}>Manual Ledger Entries</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'rows', gap: 2, justifyContent: 'space-between', minWidth: 300, py: 2, px: 1 }}>
                {/* Search Bar */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                  <TextField
                    placeholder="Search ledger entries..."
                    value={searchTerm}
                    onChange={handleSearch}
                    onKeyPress={handleSearchSubmit}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Search />
                        </InputAdornment>
                      ),
                    }}
                    size="small"
                    className='no-print'
                  />

                  {/* Date Range Selector */}
                  <FormControl size="small" sx={{ minWidth: 140 }} className='no-print'>
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={dateRange}
                      label="Date Range"
                      onChange={handleDateRangeChange}
                    >
                      <MenuItem value="all">All Time</MenuItem>
                      <MenuItem value="3months">Last 3 Months</MenuItem>
                      <MenuItem value="6months">Last 6 Months</MenuItem>
                      <MenuItem value="1year">Last 1 Year</MenuItem>
                      <MenuItem value="custom">Custom Range</MenuItem>
                    </Select>
                  </FormControl>
                  {/* Show active custom date range chip */}
                  {dateRange === 'custom' && customStartDate && customEndDate && (
                    <Box sx={{ mt: 0 }} className="no-print">
                      <Chip
                        icon={<CalendarToday />}
                        label={`${formatDate(customStartDate.toISOString())} - ${formatDate(customEndDate.toISOString())}`}
                        onDelete={() => {
                          setDateRange('all');
                          setCustomStartDate(null);
                          setCustomEndDate(null);
                        }}
                        color="primary"
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  )}
                </Box>
                {/* Manual Ledger Summary */}
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: "end" }}>
                  <Box >
                    <Typography variant="body2" color="text.secondary">
                      Manual Ledger Balance
                    </Typography>
                    <Typography variant="h6" color={manualLedgerSummary.balance >= 0 ? 'success.main' : 'error.main'} >
                      {formatCurrency(Math.abs(manualLedgerSummary.balance))}
                      <Typography component="span" variant="caption" sx={{ ml: 1 }}>
                        {manualLedgerSummary.balance >= 0 ? '(Payable)' : '(Receivable)'}
                      </Typography>
                    </Typography>
                  </Box>
                </Box>
              </Box>

              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} size="small">
                  <TableHead sx={{ bgcolor: 'grey.50' }}>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>Voucher No.</strong></TableCell>
                      <TableCell><strong>SR No.</strong></TableCell>
                      <TableCell align="right"><strong>Debit (₹)</strong></TableCell>
                      <TableCell align="right"><strong>Credit (₹)</strong></TableCell>
                      <TableCell align="right"><strong>Description</strong></TableCell>
                      <TableCell align="right"><strong>Balance (₹)</strong></TableCell>
                      <TableCell className='no-print'><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          Loading...
                        </TableCell>
                      </TableRow>
                    ) : filteredLedgers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          No ledger entries found
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredLedgers.map((ledger) => (
                        <TableRow key={ledger._id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              {/* Status dot */}
                              <Tooltip title={ledger.status === 'active' ? 'Active' : 'Inactive'} arrow>
                                <FiberManualRecord
                                  sx={{
                                    fontSize: 10,
                                    color: ledger.status === 'active' ? 'success.main' : 'error.main'
                                  }}
                                  className='no-print'
                                />
                              </Tooltip>
                              {formatDate(ledger.date)}
                            </Box>
                          </TableCell>
                          <TableCell>{ledger.voucher || '-'}</TableCell>
                          <TableCell>{ledger.srNo || '-'}</TableCell>
                          <TableCell align="right">{formatCurrency(ledger.debit)}</TableCell>
                          <TableCell align="right">{formatCurrency(ledger.credit)}</TableCell>
                          <TableCell align="right">
                            <Tooltip title={ledger.description || '-'} arrow placement="left" className='no-print'>
                              <Box sx={{
                                maxWidth: '150px',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}>
                                {ledger.description || '-'}
                              </Box>
                            </Tooltip>
                            <Box className='print-only' sx={{ maxWidth: '150px' }}>
                              {ledger.description || '-'}
                            </Box>
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              color: ledger.balance >= 0 ? 'success.main' : 'error.main',
                              fontWeight: 'bold'
                            }}
                            className={ledger.balance >= 0 ? 'balance-positive' : 'balance-negative'}
                          >
                            {formatCurrency(ledger.balance)}
                          </TableCell>
                          <TableCell className='no-print'>
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              onClick={() => handleDeleteClick(ledger._id)}
                              sx={{
                                minWidth: 'auto',
                                fontSize: '0.60rem',
                                py: 0.0,
                                px: 2,
                                borderRadius: 5,
                                border: '.8px solid #ff4d6e5e',
                                '&:hover': { backgroundColor: '#ff4d6e5e' }
                              }}
                            >
                              Delete
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>

          {/* GST Ledger */}
          <CommonLedgerTable
            entityId={vendorId}
            isPurchase={true}
            gstType="GST"
            ledgerLabel='Purchase GST Ledger'
            onTotalChange={(debit, credit, balance) => {
              setGstLedgerSummary({ totalDebit: debit, totalCredit: credit, balance });
            }}
          />

          {/* NON GST Ledger */}
          {!gstEnabled && (
            <CommonLedgerTable
              entityId={vendorId}
              isPurchase={true}
              gstType="NON-GST"
              ledgerLabel='Purchase NON GST Ledger'
              onTotalChange={(debit, credit, balance) => {
                setNonGstLedgerSummary({ totalDebit: debit, totalCredit: credit, balance });
              }}
            />
          )}

          {/* Overall Summary Card */}
          <Card sx={{ bgcolor: 'primary.50', mt: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Overall Summary</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Manual Ledger</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(manualLedgerSummary.balance)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">GST Ledger</Typography>
                  <Typography variant="body1" fontWeight="bold">
                    {formatCurrency(gstLedgerSummary.balance)}
                  </Typography>
                </Box>
                {!gstEnabled && (
                  <Box>
                    <Typography variant="body2" color="text.secondary">NON-GST Ledger</Typography>
                    <Typography variant="body1" fontWeight="bold">
                      {formatCurrency(nonGstLedgerSummary.balance)}
                    </Typography>
                  </Box>
                )}
                <Divider orientation="vertical" flexItem />
                <Box>
                  <Typography variant="body2" color="text.secondary">Total Balance</Typography>
                  <Typography 
                    variant="h5" 
                    fontWeight="bold"
                    color={overallSummary.balance >= 0 ? 'success.main' : 'error.main'}
                  >
                    {formatCurrency(Math.abs(overallSummary.balance))}
                    <Typography component="span" variant="body2" sx={{ ml: 1 }}>
                      {overallSummary.balance >= 0 ? '(Payable)' : '(Receivable)'}
                    </Typography>
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </PrintableContainer>

      {/* Custom Date Range Dialog */}
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Dialog
          open={customDateDialogOpen}
          onClose={handleCustomDateCancel}
          maxWidth="sm"
          fullWidth
          className='no-print'
        >
          <DialogTitle>Select Custom Date Range</DialogTitle>
          <DialogContent>
            <Stack spacing={3} sx={{ mt: 2 }}>
              <DatePicker
                label="Start Date"
                value={customStartDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setCustomStartDate(new Date(newValue as any));
                  } else {
                    setCustomStartDate(null);
                  }
                }}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
              <DatePicker
                label="End Date"
                value={customEndDate}
                onChange={(newValue) => {
                  if (newValue) {
                    setCustomEndDate(new Date(newValue as any));
                  } else {
                    setCustomEndDate(null);
                  }
                }}
                minDate={customStartDate || undefined}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCustomDateCancel}>Cancel</Button>
            <Button 
              onClick={handleCustomDateApply} 
              variant="contained"
              disabled={!customStartDate || !customEndDate}
            >
              Apply
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
        className='no-print'
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this ledger entry? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} color="primary">
            Cancel
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DailyLedgerList;